/**
 * `<caos-overlay>` — the ONE reusable overlay shell: a scrim + a floating panel that shares
 * scrim / focus-trap / Escape / close behaviour across every centred modal and right-side
 * drawer. Presence in the DOM IS "open" (the host mounts it when open, unmounts to close),
 * matching how the React surfaces render their dialogs.
 * (Catalog: Overlays › Overlay — the one scrim-and-panel shell behind every modal and drawer.)
 *
 * Two placements, one behaviour:
 *   • `placement="center"` (default) — a centred modal, capped at `--caos-overlay-width` (760px
 *     default, the Dragon OrderDetailModal width);
 *   • `placement="end"` — a full-height right-side drawer, `--caos-overlay-width` wide (420px
 *     default, the Dragon ActionHistoryDrawer width).
 *
 * On connect it moves focus into the panel, traps Tab within it and restores focus to the opener
 * on disconnect — all of it {@link panelFocus}, the library's ONE focus trap (CAOS-429), shared
 * with the four floating panels of CAOS-651 and with `<caos-inspector>`, which composes this
 * element rather than repeating it.
 * Clicking the scrim OR pressing Escape fires a cancelable, composed `caos-overlay-close` — the
 * host decides whether to unmount. The panel is a `role="dialog"` with `aria-modal`, labelled by
 * the `label` attribute. Styling seams: `::part(scrim)`, `::part(panel)`.
 *
 * House style follows `<caos-inspector>` (the panel surface/border/radius, the one float
 * elevation reserved for overlays) — this is the scrim + placement a plain side panel does not
 * own. An inspector that wants them asks for them by setting `modal`, which puts it inside one of
 * these rather than growing a second copy of it (CAOS-429).
 *
 * CAOS-601 — THE THREE SMALL DIALOGS ARE CALLS AGAINST THIS SHELL, NOT COMPONENTS. `alertDialog`,
 * `confirmDialog` and `promptDialog` are at the bottom of this file. The incumbent ships one
 * component for each, which is why a page that wants to ask a question first has to pick which of
 * three dialogs to import, and why a change to how a dialog behaves has to be made three times.
 * They are the same dialog with different footers — so here they are one builder with different
 * footers, on the one overlay, keeping its scrim, its focus trap, its Escape behaviour and its
 * `role="dialog"` for free. A fourth kind of small dialog is another footer, not another
 * component.
 */
import { CaosElement, RESET, define, makeSheet } from '../base-element.js';
import { deepActiveElement, panelFocus, type PanelFocus } from '../focus.js';
// The three calls below are built out of the library's own controls rather than raw markup, so a
// dialog looks like every other surface and inherits the focus, disabled and busy behaviour those
// pieces already have. Registered here rather than left to whatever else imported them first.
import './button.js';
import './text-field.js';

const sheet = makeSheet(`
  :host {
    position: fixed;
    inset: 0;
    /* A NAMED layer, not a number — see the header note on layering (CAOS-586). */
    z-index: var(--caos-layer-overlay);
    display: flex;
    /* the Dragon scrim: dimmed + a soft blur behind the panel */
    --caos-overlay-scrim: rgba(0, 0, 0, 0.6);
  }
  :host([placement="end"]) { justify-content: flex-end; align-items: stretch; }
  /* center (default): the modal floats near the top like the Dragon OrderDetailModal, not dead-centre */
  :host(:not([placement="end"])) { justify-content: center; align-items: flex-start; padding: var(--caos-space-4); }

  .scrim {
    position: absolute;
    inset: 0;
    appearance: none;
    border: 0;
    padding: 0;
    margin: 0;
    background: var(--caos-overlay-scrim);
    -webkit-backdrop-filter: blur(4px);
    backdrop-filter: blur(4px);
    cursor: pointer;
  }

  .panel {
    position: relative;
    display: flex;
    flex-direction: column;
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border-strong);
    box-shadow: var(--caos-shadow-float);
    overflow: hidden;
    min-height: 0;
  }
  /* centred modal */
  :host(:not([placement="end"])) .panel {
    width: 100%;
    max-width: var(--caos-overlay-width, 760px);
    max-height: 100%;
    border-radius: var(--caos-radius-lg);
  }
  /* right-side drawer — full height, no rounded corners on the flush edge */
  :host([placement="end"]) .panel {
    width: var(--caos-overlay-width, 420px);
    max-width: 92vw;
    height: 100%;
    border-top: 0;
    border-bottom: 0;
    border-right: 0;
  }

  .body { display: flex; flex-direction: column; min-height: 0; flex: 1 1 auto; }
`);

export class CaosOverlay extends CaosElement {
  static get observedAttributes(): string[] {
    return ['label', 'placement'];
  }

  private scrim: HTMLButtonElement;
  private panel: HTMLDivElement;
  private readonly focusControl: PanelFocus;

  private onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.requestClose();
      return;
    }
    this.focusControl.trap(e);
  };

  constructor() {
    super(sheet);

    this.scrim = document.createElement('button');
    this.scrim.type = 'button';
    this.scrim.className = 'scrim';
    this.scrim.setAttribute('part', 'scrim');
    this.scrim.setAttribute('aria-label', 'Close');
    this.scrim.addEventListener('click', () => this.requestClose());

    this.panel = document.createElement('div');
    this.panel.className = 'panel';
    this.panel.setAttribute('part', 'panel');
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'true');

    const body = document.createElement('div');
    body.className = 'body';
    body.append(document.createElement('slot'));
    this.panel.append(body);

    this.root.append(this.scrim, this.panel);
    this.focusControl = panelFocus(this.panel);
  }

  connectedCallback(): void {
    this.sync();
    // Read the opener NOW rather than inside the deferred callback below: by the time that runs,
    // focus may already have moved, and the control that opened us is the one focus must return to.
    const active = deepActiveElement();
    const opener = active instanceof HTMLElement ? active : null;
    this.panel.addEventListener('keydown', this.onKeydown);
    // Defer the initial focus one frame so slotted React content has rendered its controls.
    requestAnimationFrame(() => this.focusControl.open({ opener }));
  }

  disconnectedCallback(): void {
    this.panel.removeEventListener('keydown', this.onKeydown);
    this.focusControl.close(true);
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  /** Fire the cancelable close request (the host decides whether to unmount/hide). */
  requestClose(): void {
    this.dispatchEvent(
      new CustomEvent('caos-overlay-close', { bubbles: true, composed: true, cancelable: true }),
    );
  }

  private sync(): void {
    const label = this.getAttribute('label');
    if (label) this.panel.setAttribute('aria-label', label);
    else this.panel.removeAttribute('aria-label');
  }

}

define('caos-overlay', CaosOverlay);

declare global {
  interface HTMLElementTagNameMap {
    'caos-overlay': CaosOverlay;
  }
}

// ── The three small dialogs ─────────────────────────────────────────────────────────────────────

/**
 * The dialog body's own styles. It lives in a shadow root of its own — see `buildBody` — because it
 * is SLOTTED into the overlay, and a stylesheet cannot reach past the top of what it slots.
 */
const dialogSheet = makeSheet(`
  :host { display: block; }

  .dialog { display: flex; flex-direction: column; gap: var(--caos-space-4); padding: var(--caos-space-5); }

  .heading {
    margin: 0;
    font-family: var(--caos-font-display);
    font-size: 16px;
    font-weight: 600;
    line-height: 1.3;
    color: var(--caos-color-text);
  }

  .message {
    margin: 0;
    font-family: var(--caos-font);
    font-size: 13px;
    line-height: 1.55;
    color: var(--caos-color-text-muted);
    white-space: pre-wrap;
  }

  /* The committing answer sits at the end, where the eye finishes — and Cancel before it, so the
     destructive one is never the button under a thumb travelling to the nearest corner. */
  .footer { display: flex; justify-content: flex-end; gap: var(--caos-space-2); }
`);

/** What every one of the three is told. */
export interface CaosDialogOptions {
  /** The question, or the thing being said. Line breaks are kept. */
  message: string;
  /** The dialog's heading, and the name a screen reader announces for it. */
  heading?: string;
  /** The committing button's words. Each call has its own sensible default. */
  confirmLabel?: string;
  /** The other button's words. Ignored by `alertDialog`, which has only one. */
  cancelLabel?: string;
  /**
   * Whether committing is a destructive act. It colours the committing button and nothing else —
   * the dialog does not become a different dialog because the answer is dangerous.
   */
  destructive?: boolean;
  /** Where the overlay mounts. The document body, unless a caller is running somewhere else. */
  within?: HTMLElement;
}

/** What `promptDialog` is told on top of that. */
export interface CaosPromptOptions extends CaosDialogOptions {
  /** What the field is asking for. Falls back to the message, so the field is never unlabelled. */
  fieldLabel?: string;
  /** What the field starts with. */
  value?: string;
  placeholder?: string;
}

/** The buttons a dialog offers, in the order they are drawn. */
interface DialogButton {
  label: string;
  variant: string;
  /** What the promise settles with when this button is the answer. */
  answer: 'confirm' | 'cancel';
}

/**
 * The body, in a shadow root of its own.
 *
 * It is a plain `<div>` rather than a new custom element, because a small dialog is not a new
 * component — that is the whole point of this section. A root created by hand adopts RESET
 * alongside its own sheet, the same rule the app launcher, the navigation gadget and the object
 * caret follow, so the hiding guard reaches inside it too.
 */
function buildBody(
  options: CaosDialogOptions,
  buttons: readonly DialogButton[],
  field: HTMLElement | null,
  answer: (a: 'confirm' | 'cancel') => void,
): HTMLElement {
  const host = document.createElement('div');
  const root = host.attachShadow({ mode: 'open' });
  root.adoptedStyleSheets = [RESET, dialogSheet];

  const wrap = document.createElement('div');
  wrap.className = 'dialog';

  if (options.heading) {
    const heading = document.createElement('h2');
    heading.className = 'heading';
    heading.textContent = options.heading;
    wrap.append(heading);
  }

  const message = document.createElement('p');
  message.className = 'message';
  message.textContent = options.message;
  wrap.append(message);

  // Before the buttons, so the overlay's own "focus the first control" lands in the field.
  if (field) wrap.append(field);

  const footer = document.createElement('div');
  footer.className = 'footer';
  for (const button of buttons) {
    const el = document.createElement('caos-button');
    el.setAttribute('variant', button.variant);
    el.textContent = button.label;
    el.addEventListener('click', () => answer(button.answer));
    footer.append(el);
  }
  wrap.append(footer);

  root.append(wrap);
  return host;
}

/**
 * Open one, wait for the answer, take it down again.
 *
 * Every way of dismissing it lands in the same place: a button, the scrim, and Escape all settle
 * the promise exactly once and unmount the overlay. `settled` is what makes that true — an
 * overlay that is clicked and then Escaped raises the close event twice, and a promise cannot
 * resolve twice.
 */
function openDialog(
  options: CaosDialogOptions,
  buttons: readonly DialogButton[],
  field: HTMLElement | null,
): Promise<'confirm' | 'cancel'> {
  return new Promise((resolve) => {
    const overlay = document.createElement('caos-overlay');
    // The heading names the dialog; with none, the message is what there is to announce.
    overlay.setAttribute('label', options.heading ?? options.message);

    let settled = false;
    const answer = (a: 'confirm' | 'cancel'): void => {
      if (settled) return;
      settled = true;
      overlay.remove();
      resolve(a);
    };

    // The scrim and Escape both arrive here. A small dialog is dismissible, so they answer
    // `cancel` rather than being refused — and `alertDialog` treats cancel and confirm alike.
    overlay.addEventListener('caos-overlay-close', () => answer('cancel'));
    overlay.append(buildBody(options, buttons, field, answer));
    (options.within ?? document.body).append(overlay);
  });
}

/**
 * Say something and wait for it to be acknowledged. One button, and no answer to read: every way
 * out of it means the same thing.
 */
export async function alertDialog(options: CaosDialogOptions): Promise<void> {
  await openDialog(options, [{ label: options.confirmLabel ?? 'OK', variant: 'primary', answer: 'confirm' }], null);
}

/** Ask before going ahead. `true` only if the committing button was the answer. */
export async function confirmDialog(options: CaosDialogOptions): Promise<boolean> {
  const answer = await openDialog(
    options,
    [
      { label: options.cancelLabel ?? 'Cancel', variant: 'secondary', answer: 'cancel' },
      {
        label: options.confirmLabel ?? 'Confirm',
        variant: options.destructive === true ? 'destructive' : 'primary',
        answer: 'confirm',
      },
    ],
    null,
  );
  return answer === 'confirm';
}

/**
 * Ask for something and read it back. The text as typed if the committing button was the answer,
 * and `null` if it was not — which is how a caller tells "they typed nothing" from "they said no",
 * two answers an empty string cannot keep apart.
 */
export async function promptDialog(options: CaosPromptOptions): Promise<string | null> {
  const field = document.createElement('caos-text-field');
  field.setAttribute('label', options.fieldLabel ?? options.message);
  if (options.placeholder !== undefined) field.setAttribute('placeholder', options.placeholder);
  if (options.value !== undefined) field.setAttribute('value', options.value);

  const answer = await openDialog(
    options,
    [
      { label: options.cancelLabel ?? 'Cancel', variant: 'secondary', answer: 'cancel' },
      {
        label: options.confirmLabel ?? 'OK',
        variant: options.destructive === true ? 'destructive' : 'primary',
        answer: 'confirm',
      },
    ],
    field,
  );
  if (answer !== 'confirm') return null;
  return (field as HTMLElement & { value?: string }).value ?? '';
}
